import { CheckCircleFilled, CopyOutlined, LockOutlined } from "@ant-design/icons";
import { Alert, Button, Form, Input, Select, Space, Typography, message } from "antd";
import { useState } from "react";
import { TONE } from "../../components/Compliance/findings.shared";
import { type SetupPortal, useSubmitEhrLoginMutation } from "../../service/onboarding.service";
import { ehrOptions } from "../onboarding/onboarding.constant";
import styles from "./setup.module.scss";
import { INDIGO_TONE, apiErrorMessage } from "./setup.shared";

const { Text, Paragraph } = Typography;

/** The same EHR list the signup flow offers. */
const EHR_SELECT_OPTIONS = ehrOptions.map(({ label }) => ({ label, value: label }));
const OTHER_EHR = "Other";

type Props = {
  token: string;
  portal: SetupPortal;
};

const CopyableValue = ({ label, value }: { label: string; value: string }) => (
  <div className={styles.identifierRow}>
    <span className={styles.identifierLabel}>{label}</span>
    <code className={styles.identifierValue}>{value}</code>
    <Button
      size="small"
      type="text"
      icon={<CopyOutlined />}
      onClick={() => {
        navigator.clipboard?.writeText(value);
        message.success(`${label} copied`);
      }}
    >
      Copy
    </Button>
  </div>
);

/** Step 3 — create an EHR login for Nudge. */
export default function EhrLoginForm({ token, portal }: Props) {
  const [submit, { isLoading }] = useSubmitEhrLoginMutation();
  // "Other" alone tells staff nothing, so it opens a free-text field.
  const [isOtherEhr, setIsOtherEhr] = useState(false);
  const submitted = portal.ehr.submitted ? portal.ehr : null;
  const isBlocked = submitted?.verification_status === "failed";
  // Show the form when nothing is on file, or when we couldn't log in.
  const [isReplacing, setIsReplacing] = useState(false);
  const showForm = !submitted || isBlocked || isReplacing;
  // Green once we've confirmed the login works; Nudge indigo while it's still on us.
  const verifiedTone = submitted?.verification_status === "verified" ? TONE.success : INDIGO_TONE;

  const onSubmit = async ({
    ehr_other,
    ...values
  }: {
    ehr_name: string;
    ehr_other?: string;
    login_url?: string;
    username: string;
    password: string;
    auth_notes?: string;
    priority_templates?: (string | undefined)[];
  }) => {
    try {
      const ehr_name = values.ehr_name === OTHER_EHR ? (ehr_other || "").trim() : values.ehr_name;
      // Inputs are bound by index; JSON.stringify turns the holes into `null`, which the API rejects.
      const priority_templates = (values.priority_templates ?? []).map((name) => (name ?? "").trim()).filter(Boolean);
      await submit({ token, ...values, ehr_name, priority_templates }).unwrap();
      message.success("Got it — we'll verify the login and take it from here.");
      setIsReplacing(false);
    } catch (err) {
      message.error(apiErrorMessage(err, "We couldn't save that. Please try again."));
    }
  };

  const { integration } = portal;

  return (
    <>
      {/* ── 3a: what we give them ───────────────────────────────────────── */}
      <p className={styles.sectionLabel}>First — create the login in your EHR</p>
      <Paragraph className={styles.blurb} style={{ marginBottom: 12 }}>
        Create a <strong>new user</strong> in your EHR using exactly these details, so our system can complete the
        activation automatically:
      </Paragraph>

      <div className={styles.identifiers}>
        {integration.email ? (
          <CopyableValue label="Email" value={integration.email} />
        ) : (
          <Alert
            type="info"
            showIcon
            message="We're provisioning a dedicated email address for your EHR user."
            description="You'll get an email the moment it's ready. Please don't use your own address: your EHR will send activation links and login codes here, and our system needs to read them."
            style={{ marginBottom: integration.phone ? 12 : 0 }}
          />
        )}
        {integration.phone ? (
          <CopyableValue label="Phone" value={integration.phone} />
        ) : (
          <Text type="secondary" style={{ fontSize: 12, display: "block", marginTop: 10 }}>
            If your EHR requires a phone number for two-factor codes, we'll send you a dedicated number to use — just
            tell us in the notes below which method it uses.
          </Text>
        )}
        {integration.email ? (
          <Text type="secondary" style={{ fontSize: 12, display: "block", marginTop: 10 }}>
            Use these exactly — not your own address. Your EHR sends activation links and login codes here, and our
            system needs to read them.
          </Text>
        ) : null}
      </div>

      <Paragraph className={styles.blurb}>
        Give this user the same permissions a clinician or medical assistant would have — enough to open charts, view
        your note templates, and create/transfer notes.
      </Paragraph>

      {/* ── 3b: what they give us ───────────────────────────────────────── */}
      <p className={styles.sectionLabel}>Then — send us the login details</p>

      {/* What the account itself needs, stated where they are about to hand it over. */}
      <Alert
        type="info"
        showIcon={false}
        style={{ marginBottom: 20 }}
        message="The account should have — this helps us test, and you can send the login before it's all in place."
        description={
          <ul className={styles.prepList}>
            <li>
              <strong>2 test patient profiles</strong> — fake names are fine
            </li>
            <li>
              <strong>Your custom note templates</strong> loaded in the account — the ones you want us to prioritise
            </li>
            <li>
              <strong>2–3 open encounters on each test patient</strong> — unsigned, unclosed notes we can write into.
              Signed or closed encounters are locked, so we can't test against them.
            </li>
          </ul>
        }
      />

      {isBlocked ? (
        <Alert
          type="error"
          showIcon
          message="We couldn't log in with those details"
          description={
            submitted?.verification_detail || "Please double-check the username and password and re-submit below."
          }
          style={{ marginBottom: 20 }}
        />
      ) : null}

      {submitted && !showForm ? (
        <Alert
          type="success"
          showIcon
          icon={<CheckCircleFilled />}
          message={
            <Space direction="vertical" size={2}>
              <span>
                Login details received
                {/* Same pill treatment as the step statuses, so this reads as one system. */}
                <span
                  className={styles.statusPill}
                  style={{
                    marginLeft: 8,
                    background: verifiedTone.bg,
                    color: verifiedTone.fg,
                    border: `1px solid ${verifiedTone.border}`,
                  }}
                >
                  <span className={styles.statusDot} style={{ background: verifiedTone.accent }} />
                  {submitted.verification_status === "verified" ? "Verified" : "Checking"}
                </span>
              </span>
              <Text type="secondary" style={{ fontSize: 12 }}>
                We don't show them back here. Replacing them starts from a blank form.
              </Text>
            </Space>
          }
          action={
            <Button size="small" type="link" onClick={() => setIsReplacing(true)}>
              Replace
            </Button>
          }
        />
      ) : null}

      {showForm ? (
        <Form layout="vertical" onFinish={onSubmit} requiredMark={false}>
          <Form.Item
            label="Which EHR do you use?"
            name="ehr_name"
            rules={[{ required: true, message: "Please tell us which EHR you use." }]}
          >
            <Select
              showSearch
              placeholder="Select your EHR"
              optionFilterProp="label"
              options={EHR_SELECT_OPTIONS}
              onChange={(value) => setIsOtherEhr(value === OTHER_EHR)}
            />
          </Form.Item>
          {isOtherEhr ? (
            <Form.Item
              label="Which one?"
              name="ehr_other"
              rules={[{ required: true, message: "Please tell us the name of your EHR." }]}
            >
              <Input placeholder="e.g. Tebra" />
            </Form.Item>
          ) : null}
          <Form.Item label="Login URL" name="login_url">
            <Input placeholder="https://yourpractice.ehr.com/login" />
          </Form.Item>
          <Form.Item
            label="Username"
            name="username"
            rules={[{ required: true, message: "Please enter the username you created." }]}
          >
            <Input autoComplete="off" />
          </Form.Item>
          <Form.Item
            label="Password"
            name="password"
            rules={[{ required: true, message: "Please enter the password you set." }]}
          >
            <Input.Password autoComplete="new-password" />
          </Form.Item>
          <p className={styles.sectionLabel} style={{ marginTop: 4 }}>
            Your five most-used note templates
          </p>
          <Text type="secondary" style={{ fontSize: 12, display: "block", marginBottom: 12 }}>
            Name them as they appear in your EHR. We build these first, matching the formats your practice already uses.
          </Text>
          {[0, 1, 2, 3, 4].map((index) => (
            <Form.Item key={index} name={["priority_templates", index]} style={{ marginBottom: 12 }}>
              <Input
                placeholder={
                  [
                    "e.g. Follow-up Visit",
                    "e.g. New Patient Intake",
                    "e.g. Annual Physical",
                    "e.g. Telehealth Check-in",
                    "e.g. Procedure Note",
                  ][index]
                }
                autoComplete="off"
              />
            </Form.Item>
          ))}

          <Form.Item
            label="Anything unusual about your login?"
            name="auth_notes"
            extra="Single sign-on, IP allowlisting, VPN, short session timeouts, or which two-factor method it uses (app, SMS, email). Please don't put passwords, PINs or security-question answers here."
          >
            <Input.TextArea
              rows={3}
              placeholder="e.g. 'SSO via Okta', 'two-factor by SMS', 'sessions time out after 15 min'"
            />
          </Form.Item>

          <Space>
            <Button type="primary" htmlType="submit" loading={isLoading}>
              {submitted ? "Submit new details" : "Send securely"}
            </Button>
            {isReplacing ? <Button onClick={() => setIsReplacing(false)}>Cancel</Button> : null}
          </Space>
        </Form>
      ) : null}

      <Text type="secondary" style={{ fontSize: 12, display: "block", marginTop: 14 }}>
        <LockOutlined /> Encrypted in transit and at rest. Your password is never shown again after you submit it — not
        even to us in a support screen.
      </Text>
    </>
  );
}
